// components/ui/FeedbackWidget.jsx
import { useState } from "react";

// Icon components
const SmileIcon = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
    <line x1="9" y1="9" x2="9.01" y2="9"/>
    <line x1="15" y1="9" x2="15.01" y2="9"/>
  </svg>
);

const MehIcon = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="8" y1="15" x2="16" y2="15"/>
    <line x1="9" y1="9" x2="9.01" y2="9"/>
    <line x1="15" y1="9" x2="15.01" y2="9"/>
  </svg>
);

const FrownIcon = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M16 16s-1.5-2-4-2-4 2-4 2"/>
    <line x1="9" y1="9" x2="9.01" y2="9"/>
    <line x1="15" y1="9" x2="15.01" y2="9"/>
  </svg>
);

const MessageIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

export default function FeedbackWidget({ siteId, sessionId }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState("neutral");
  const [category, setCategory] = useState("Idea");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  // Context-aware placeholders based on category
  const getPlaceholder = () => {
    switch (category) {
      case "Bug":
        return "What went wrong? Steps to reproduce would help us fix it faster.";
      case "Idea":
        return "What feature or improvement would make your life easier?";
      case "Dashboard":
        return "What's missing or confusing on the dashboard?";
      case "Reports":
        return "What data or insights would you like to see?";
      case "Integrations":
        return "Which integration needs work, or what should we add?";
      case "Usability":
        return "What's frustrating or hard to find?";
      case "Pricing":
        return "What pricing concerns or questions do you have?";
      case "Setup":
        return "What part of setup was confusing or broken?";
      case "Tracking":
        return "What tracking issue are you experiencing?";
      case "Heatmap":
        return "What would make heatmaps more useful for you?";
      case "Sessions":
        return "What's missing from session recordings?";
      case "Funnels":
        return "How could funnel analysis work better for you?";
      default:
        return "Tell us what's on your mind...";
    }
  };

  async function submit() {
    const API_URL = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';
    
    const payload = {
      site_id: siteId,
      session_id: sessionId,
      rating,
      category,
      message,
      email: email || null,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
    };

    try {
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (res.ok) {
        try {
          await fetch(`${API_URL}/api/collect`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({
              site_id: siteId,
              events: [{
                event_type: "feedback_submitted",
                page_path: window.location.pathname,
                raw_payload: payload,
              }]
            }),
            credentials: "include",
          });
        } catch {}

        setOpen(false);
        setRating("neutral");
        setCategory("Idea");
        setMessage("");
        setEmail("");
        alert("Thanks! Your feedback just made our backlog smarter.");
      } else {
        alert("Couldn't send feedback. Mind trying again?");
      }
    } catch (error) {
      alert("Couldn't send feedback. Mind trying again?");
    }
  }

  const ratingOptions = [
    { val: "happy", icon: SmileIcon, title: "Love it", color: "#22c55e" },
    { val: "neutral", icon: MehIcon, title: "It's okay", color: "#f59e0b" },
    { val: "sad", icon: FrownIcon, title: "Frustrated", color: "#ef4444" },
  ];

  return (
    <>
      <div className="feedback-widget-container">
        <button
          className="feedback-floating-btn"
          onClick={() => setOpen(true)}
          title="Send Feedback"
          type="button"
        >
          <MessageIcon size={20} color="white" />
        </button>
      </div>

      {open && (
        <div className="feedback-modal-overlay" onClick={() => setOpen(false)}>
          <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h2>Help Improve BaoBrain</h2>
              <button 
                onClick={() => setOpen(false)} 
                className="feedback-modal-close"
                type="button"
              >
                ×
              </button>
            </div>

            <p className="feedback-subtitle">
              Got feedback? We read every submission and use it to prioritize what we build next.
            </p>

            <div className="feedback-modal-body">
              <div className="feedback-field">
                <label className="feedback-label">How's your experience?</label>
                <div className="feedback-rating-options">
                  {ratingOptions.map(o => (
                    <button
                      key={o.val}
                      onClick={() => setRating(o.val)}
                      className={`feedback-rating-btn ${rating === o.val ? "active" : ""}`}
                      title={o.title}
                      type="button"
                      style={{
                        '--active-color': o.color,
                      }}
                    >
                      <o.icon 
                        size={28} 
                        color={rating === o.val ? o.color : "#9ca3af"} 
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="feedback-field">
                <label className="feedback-label">What's this about?</label>
                <select
                  className="feedback-select"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                >
                  <optgroup label="Feedback Type">
                    <option>Bug</option>
                    <option>Idea</option>
                    <option>Other</option>
                  </optgroup>
                  <optgroup label="Product Area">
                    <option>Dashboard</option>
                    <option>Reports</option>
                    <option>Integrations</option>
                    <option>Usability</option>
                    <option>Pricing</option>
                  </optgroup>
                  <optgroup label="Specific Features">
                    <option>Setup</option>
                    <option>Tracking</option>
                    <option>Heatmap</option>
                    <option>Sessions</option>
                    <option>Funnels</option>
                  </optgroup>
                </select>
              </div>

              <div className="feedback-field">
                <label className="feedback-label">Tell us more</label>
                <textarea
                  className="feedback-textarea"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={getPlaceholder()}
                />
              </div>

              <div className="feedback-field">
                <label className="feedback-label">Email <span className="feedback-optional">(optional)</span></label>
                <input
                  className="feedback-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                />
                <p className="feedback-helper">We'll only use this to follow up on your feedback.</p>
              </div>
            </div>

            <div className="feedback-modal-footer">
              <button 
                className="feedback-btn feedback-btn-secondary" 
                onClick={() => setOpen(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="feedback-btn feedback-btn-primary"
                onClick={submit}
                disabled={!message.trim()}
                type="button"
              >
                Send Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}